import { Component, OnInit, HostListener, ViewChild, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { LatestUpdate, LatestUpdateType } from "../../models/latestupdate";
import { LatestUpdateService } from "../../services/latestupdate.service";
import { ICountry } from "../../interfaces/icountry";
import { DropDownItem } from "../../models/dropdown";
import { AppSettings } from "../../appsettings";
import { TrimmedMinLengthValidator } from "../../validators/trimmedMinLength.validator";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { CkEditorEnhancedDirective } from "../../directives/ck-editor-enhanced.directive";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { HeaderComponent } from "../header/header.component";
import { NgSelectComponent } from "../shared/ng-select/ng-select.component";


@Component({
    selector: "latestupdate",
    templateUrl: "./latestupdate.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationModalComponent, CKEditorModule, CkEditorEnhancedDirective, HeaderComponent, NgSelectComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class LatestUpdateComponent implements OnInit, ICanComponentDeactivate {
    latestUpdate: LatestUpdate;
    latestUpdateForm: FormGroup;
    allCountries: ICountry[] = new Array();
    countries: DropDownItem[] = new Array();
    config = {};
    public Editor = ClassicEditor;
    private latestUpdateType = LatestUpdateType;
    private businessfunctionId: number;
    emailFeatureEnabled: boolean = false;
    public title: string;
    @ViewChild("confirmModal") confirmModal;

    constructor(private activatedRoute: ActivatedRoute,
        private router: Router,
        private latestUpdateService: LatestUpdateService,
        private toastsManager: ToastrService,
        private businessFunctionService: BusinessFunctionService,
        private store: Store<IAppStore>) {
        this.config = AppSettings.ckeditorConfig;
    }
    /**
     * On initailse of a latest update component.
     * Here it initailse the form contents and load the data to display.
     */
    ngOnInit(): void {
        this.store.select(e => e.user).subscribe(e => {
            if (e.selectedBusinessFunction) {
                this.businessfunctionId = e.selectedBusinessFunction.id;
            }
        });
        this.businessFunctionService.getBusinessFunction(this.businessfunctionId).subscribe(
            data => {
                data.features.forEach(obj => {
                    if (obj.featureId == 8) {
                        this.emailFeatureEnabled = obj.enabled;
                    }
                });
            }); 
        this.latestUpdate = this.activatedRoute.snapshot.data["latestupdate"] as LatestUpdate;
        this.allCountries = this.activatedRoute.snapshot.data["countries"] as ICountry[];

        if (this.allCountries != null) {
            this.loadCountryDropDown();
        }
        this.latestUpdateForm = this.initFormGroup();
        this.title = this.latestUpdate ? "Edit latest update" : "Create latest update";
    }

    /**
     * Initialise the Form Group depending on the type of latest update
     */
    initFormGroup(): FormGroup {
        let url: string = null;
        let countryId: string;
        let disabled: boolean = false;
        let latestUdpateContent: string = "";
        let latestUpdateType: number = LatestUpdateType.GenericLatestUpdate;

        if (this.latestUpdate != null) {
            if (this.latestUpdate.countryId != null) {
                countryId = this.latestUpdate.countryId.toString();
            }
            url = this.latestUpdate.url;
            disabled = this.latestUpdate.disabled;
            latestUdpateContent = this.latestUpdate.description;
            latestUpdateType = this.latestUpdate.latestUpdateType;
        }

        let urlValidators: any = new Array();
        // only apply validation if creating new latest update
        // this is because we do not allow the url to be edited
        if (latestUpdateType === LatestUpdateType.GenericLatestUpdate && this.latestUpdate == null) {
            urlValidators.push(Validators.pattern(RegExp(/((^http(s)?)((:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)?/).source));
        }
        return new FormGroup({
            countryId: new FormControl(countryId),
            description: new FormControl(latestUdpateContent, [Validators.required, TrimmedMinLengthValidator(3)]),
            url: new FormControl(url, urlValidators),
            disabled: new FormControl(disabled),
            sendEmail: new FormControl(false)
        });
    }

    /**
     * Load Country dropdown
     */
    loadCountryDropDown(): void {
        this.countries = this.allCountries.map((e: ICountry) => new DropDownItem(e.id, e.name, null));
    }

    /**
     * Fires on selection of a country dropdown
     * @param event The selected country value.
     */
    countrySelected(event: DropDownItem): void {
        (<FormControl>this.latestUpdateForm.controls["countryId"])
            .setValue(event.id, { onlySelf: false });
    }

    /**
     * Fires on removed of a country dropdown
     */
    countryRemoved(): void {
        (<FormControl>this.latestUpdateForm.controls["countryId"])
            .setValue(null, { onlySelf: false });
    }

    /**
     * Save latest update. It creates a new latest udpate record if id is null or updates the existing one if it is already present.
     * @param latestUpdate Form values.
     */
    saveLatestUpdate(latestUpdate: LatestUpdate): void {
        if (this.latestUpdate == null) {
            let businessFunction: IBusinessFunction = null;

            this.store.select(e => e.user.selectedBusinessFunction)
                .subscribe(e => businessFunction = e);

            latestUpdate.latestUpdateType = LatestUpdateType.GenericLatestUpdate;
            latestUpdate.businessFunctionId = businessFunction.id;

            // if a url has not been provided, but a country has been selected
            // we need to default the url to the business functions url
            if (latestUpdate.countryId != null && (latestUpdate.url == null || latestUpdate.url.trim().length < 1)) {
                const countryCode: string = this.allCountries.filter(c => c.id === latestUpdate.countryId)[0].code;
                latestUpdate.url = `${businessFunction.defaultRoute}/countryinformation/${countryCode}`;
            }

            this.latestUpdateService.create(latestUpdate).subscribe(() => {
                this.toastsManager.success("Latest update has been created.");
                this.latestUpdateForm.reset();
                this.router.navigate(["/latestupdates"]);
            });
        } else {
            if (this.latestUpdate.latestUpdateType === LatestUpdateType.GenericLatestUpdate) {
                this.latestUpdate.countryId = latestUpdate.countryId;
                this.latestUpdate.url = latestUpdate.url;
            }
            this.latestUpdate.description = latestUpdate.description;
            this.latestUpdate.disabled = latestUpdate.disabled;
            this.latestUpdate.sendEmail = latestUpdate.sendEmail;
            this.latestUpdateService.update(this.latestUpdate).subscribe(() => {
                this.toastsManager.success("Latest update has been updated.");
                this.latestUpdateForm.reset();
                this.router.navigate(["/latestupdates"]);
            });
        }
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.latestUpdateForm.dirty) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.latestUpdateForm.reset();
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }

    navigatingTo: string;
}


